// src/pages/Auth/index.jsx
import React, { useState } from 'react';
import { Card, Form, Input, Button, Divider, message, Typography } from 'antd';
import { UserOutlined, LockOutlined, GithubOutlined, WechatOutlined, QqOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';  // 添加AuthContext导入
import { useLocation, useNavigate } from 'react-router-dom';  // 添加导航相关导入
import './index.css';

const { Title, Text } = Typography;

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();  // 从AuthContext获取方法
  const location = useLocation();
  const navigate = useNavigate();
  
  // 获取重定向路径
  const from = location.state?.from?.pathname || '/';

  // 登录处理函数
  const handleLogin = async (values) => {
    setLoading(true);
    
    try {
      const result = login(values.username, values.password);
      
      if (result.success) {
        message.success(result.message);
        navigate(from);  // 登录成功后跳转到原页面
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('登录失败');
    } finally {
      setLoading(false);
    }
  };

  // 注册处理函数
  const handleRegister = async (values) => {
    setLoading(true);
    
    try {
      const userData = {
        username: values.username,
        password: values.password,
        email: values.email,
        name: values.username, // 使用用户名作为姓名
        avatar: 'https://via.placeholder.com/100',
        skills: 'JavaScript, React',
        interests: '编程, 阅读, 旅行',
        github: '',
        homepage: '',
        description: '欢迎来到我的博客！'
      };
      
      const result = register(userData);
      
      if (result.success) {
        message.success(result.message);
        // 注册成功后切换到登录界面
        setIsLogin(true);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('注册失败');
    } finally {
      setLoading(false);
    }
  };

  // 社交登录处理函数
  const handleSocialLogin = (socialType) => {
    message.info(`${socialType}登录功能开发中...`);
  };

  // 表单提交处理
  const onFinish = async (values) => {
    if (isLogin) {
      await handleLogin(values);
    } else {
      await handleRegister(values);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '70vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #fff5f5, #fffbeb, #fff5f5)'
    }}>
      <Card 
        style={{ 
          maxWidth: 420, 
          width: '100%',
          boxShadow: '0 8px 32px rgba(220, 38, 38, 0.2)',
          margin: '0 auto',
          borderRadius: '20px',
          border: '1px solid #fecaca'
        }}
        bodyStyle={{
          padding: '30px'
        }}
        title={
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '15px',
              background: 'linear-gradient(135deg, #dc2626, #d97706)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {isLogin ? '🔐' : '✍️'}
            </div>
            <Title level={2} style={{ margin: 0, color: '#7f1d1d' }}>
              {isLogin ? '登录到博客' : '注册新账户'}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginTop: '8px', color: '#dc2626' }}>
              {isLogin ? '欢迎回来，开始您的创作之旅' : '加入我们，开启您的博客生涯'}
            </Text>
          </div>
        }
      >
        <Form
          name="auth"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          {/* 用户名输入框 */}
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入您的用户名!' }]}
            label={
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#7f1d1d' }}>
                <UserOutlined style={{ marginRight: '6px', color: '#dc2626' }} />
                用户名
              </span>
            }
          >
            <Input 
              prefix={<UserOutlined style={{ color: '#f87171' }} />}
              placeholder="请输入用户名" 
              size="large"
              style={{ 
                padding: '12px 15px',
                fontSize: '15px',
                borderRadius: '12px',
                border: '2px solid #fecaca',
                background: '#fff5f5'
              }}
            />
          </Form.Item>

          {/* 密码输入框 */}
          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入您的密码!' }]}
            label={
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#7f1d1d' }}>
                <LockOutlined style={{ marginRight: '6px', color: '#dc2626' }} />
                密码
              </span>
            }
          >
            <Input.Password 
              prefix={<LockOutlined style={{ color: '#f87171' }} />}
              placeholder="请输入密码" 
              size="large"
              style={{ 
                padding: '12px 15px',
                fontSize: '15px',
                borderRadius: '12px',
                border: '2px solid #fecaca',
                background: '#fff5f5'
              }}
            />
          </Form.Item>

          {/* 邮箱输入框（仅在注册时显示） */}
          {!isLogin && (
            <Form.Item
              name="email"
              rules={[
                { required: true, message: '请输入您的邮箱!' },
                { type: 'email', message: '请输入有效的邮箱地址!' }
              ]}
              label={
                <span style={{ fontSize: '15px', fontWeight: '500', color: '#7f1d1d' }}>
                  <MailOutlined style={{ marginRight: '6px', color: '#dc2626' }} />
                  邮箱
                </span>
              }
            >
              <Input 
                prefix={<MailOutlined style={{ color: '#f87171' }} />}
                placeholder="请输入邮箱地址" 
                size="large"
                style={{ 
                  padding: '12px 15px',
                  fontSize: '15px',
                  borderRadius: '12px',
                  border: '2px solid #fecaca',
                  background: '#fff5f5'
                }}
              />
            </Form.Item>
          )}

          {/* 提交按钮 */}
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              style={{ 
                width: '100%', 
                height: '50px',
                fontSize: '16px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #dc2626, #d97706)',
                border: 'none',
                borderRadius: '12px'
              }}
            >
              {isLogin ? '登录到我的博客' : '创建我的博客账户'}
            </Button>
          </Form.Item>
        </Form>

        {/* 社交登录选项 */}
        <Divider style={{ margin: '25px 0', color: '#fbbf24' }}>
          <span style={{ color: '#d97706', fontSize: '14px' }}>或使用社交媒体账户登录</span>
        </Divider>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '1.2rem',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <Button 
            icon={<GithubOutlined style={{ fontSize: '22px', color: '#dc2626' }} />}
            onClick={() => handleSocialLogin('github')}
            disabled={loading}
            style={{ 
              borderColor: '#fecaca',
              borderRadius: '50%',
              width: '52px',
              height: '52px',
              backgroundColor: '#fff5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
              transition: 'all 0.3s ease'
            }}
            title="GitHub 登录"
          />
          <Button 
            icon={<WechatOutlined style={{ fontSize: '22px', color: '#dc2626' }} />}
            onClick={() => handleSocialLogin('wechat')}
            disabled={loading}
            style={{ 
              borderColor: '#fecaca',
              borderRadius: '50%',
              width: '52px',
              height: '52px',
              backgroundColor: '#fff5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
              transition: 'all 0.3s ease'
            }}
            title="微信登录"
          />
          <Button 
            icon={<QqOutlined style={{ fontSize: '22px', color: '#dc2626' }} />}
            onClick={() => handleSocialLogin('qq')}
            disabled={loading}
            style={{ 
              borderColor: '#fecaca',
              borderRadius: '50%',
              width: '52px',
              height: '52px',
              backgroundColor: '#fff5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
              transition: 'all 0.3s ease'
            }}
            title="QQ 登录"
          />
        </div>

        <Divider style={{ margin: '20px 0' }}/>
        
        <div style={{ textAlign: 'center' }}>
          <Text style={{ color: '#7f1d1d', fontSize: '15px' }}>
            {isLogin ? '还没有账户？' : '已有账户？'}
            <Button 
              type="link" 
              onClick={() => setIsLogin(!isLogin)}
              style={{ 
                padding: 0, 
                marginLeft: 8,
                color: '#d97706',
                fontWeight: '600',
                fontSize: '15px'
              }}
            >
              {isLogin ? '立即创建账户' : '立即登录'}
            </Button>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Auth;